import { Color, Data3DTexture, NearestFilter, RGBAFormat, UnsignedByteType } from 'three';
import arctic from '../../content/palettes/arctic.json';
import core from '../../content/palettes/core.json';
import desert from '../../content/palettes/desert.json';
import temperate from '../../content/palettes/temperate.json';
import dawn from '../../content/palettes/grades/dawn.json';
import day from '../../content/palettes/grades/day.json';
import dusk from '../../content/palettes/grades/dusk.json';
import night from '../../content/palettes/grades/night.json';
import { hexToRgb8, nearestPaletteIndex, rgb8ToOklab, type Rgb8 } from './color';

export type BiomeName = 'arctic' | 'desert' | 'temperate';
export type TimeOfDay = 'dawn' | 'day' | 'dusk' | 'night';

/** A colour reference: `ramp.index` (0 = darkest) or `ramp.lo | mid | hi`. */
export type ColorRef = string;

interface PaletteFile {
  name: string;
  ramps: Record<string, string[]>;
  haze?: string;
  ambientNight?: string;
}

export interface Grade {
  name: string;
  sunDir: [number, number, number];
  sunColor: string;
  sunIntensity: number;
  skyColor: string;
  groundColor: string;
  hemiIntensity: number;
  hazeAmount: number;
  tint: string;
  tintAmount: number;
  valueScale: number;
}

const BIOMES: Record<BiomeName, PaletteFile> = { arctic, desert, temperate };
const GRADES = { dawn, day, dusk, night } as unknown as Record<TimeOfDay, Grade>;

export const LUT_SIZE = 32;

export class Palette {
  readonly biome: BiomeName;
  readonly ramps: Readonly<Record<string, readonly string[]>>;
  readonly haze: string;
  private readonly cache = new Map<ColorRef, Color>();

  constructor(biome: BiomeName) {
    const file = BIOMES[biome];
    this.biome = biome;
    this.ramps = { ...core.ramps, ...file.ramps };
    this.haze = file.haze ?? '#ffffff';
  }

  /** Hex string for a colour reference. Throws on an unknown ramp so typos fail loudly. */
  hex(ref: ColorRef): string {
    const [rampName, step = 'mid'] = ref.split('.');
    const ramp = rampName === undefined ? undefined : this.ramps[rampName];
    if (!ramp || ramp.length === 0) throw new Error(`unknown palette ramp in "${ref}"`);
    let index: number;
    if (step === 'lo') index = 0;
    else if (step === 'hi') index = ramp.length - 1;
    else if (step === 'mid') index = Math.floor((ramp.length - 1) / 2);
    else index = Math.min(ramp.length - 1, Math.max(0, Number.parseInt(step, 10) || 0));
    return ramp[index] ?? '#ff00ff';
  }

  /** Linear-space three.js colour (shared instance: do not mutate). */
  color(ref: ColorRef): Color {
    let c = this.cache.get(ref);
    if (!c) {
      c = new Color(this.hex(ref));
      this.cache.set(ref, c);
    }
    return c;
  }

  /** Every colour in the active palette, sRGB 8-bit. */
  all(): Rgb8[] {
    const out: Rgb8[] = [];
    for (const ramp of Object.values(this.ramps)) for (const hex of ramp) out.push(hexToRgb8(hex));
    out.push(hexToRgb8(this.haze));
    return out;
  }

  /**
   * Quantisation LUT: an sRGB-indexed 3D texture whose every cell holds the nearest
   * palette colour (nearest in OKLab). Sampled with nearest filtering in the post pass.
   */
  buildLut(extra: readonly Rgb8[] = []): Data3DTexture {
    const colors = [...this.all(), ...extra];
    const labs = colors.map(rgb8ToOklab);
    const n = LUT_SIZE;
    const data = new Uint8Array(n * n * n * 4);
    let i = 0;
    for (let b = 0; b < n; b++) {
      for (let g = 0; g < n; g++) {
        for (let r = 0; r < n; r++) {
          const cell: Rgb8 = [
            Math.round((r / (n - 1)) * 255),
            Math.round((g / (n - 1)) * 255),
            Math.round((b / (n - 1)) * 255),
          ];
          const hit = colors[nearestPaletteIndex(rgb8ToOklab(cell), labs)] ?? cell;
          data[i++] = hit[0];
          data[i++] = hit[1];
          data[i++] = hit[2];
          data[i++] = 255;
        }
      }
    }
    const tex = new Data3DTexture(data, n, n, n);
    tex.format = RGBAFormat;
    tex.type = UnsignedByteType;
    tex.minFilter = NearestFilter;
    tex.magFilter = NearestFilter;
    tex.unpackAlignment = 1;
    tex.needsUpdate = true;
    return tex;
  }
}

export function getGrade(tod: TimeOfDay): Grade {
  return GRADES[tod];
}
